import { Link, useLocation, useParams } from 'react-router-dom';

import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { GetCaseOverviewRes } from '@/api/types/queries';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';

const FactsOverViewLoader = () => {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Skeleton className="h-4 w-16 bg-gray-200" />
        <Skeleton className="h-4 w-12 bg-gray-200" />
        <Skeleton className="h-4 w-20 bg-gray-200" />
        <Skeleton className="h-4 w-14 bg-gray-200" />
      </div>
      <Skeleton className="h-8 w-full bg-gray-200" />
    </div>
  );
};

interface FactsOverviewProps {
  overview: GetCaseOverviewRes | undefined;
  isLoading: boolean;
  onCreateChronology: () => void;
  totalInconsistencies: number;
}

export const FactsOverview = ({
  isLoading,
  overview,
  onCreateChronology,
  totalInconsistencies,
}: FactsOverviewProps) => {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const location = useLocation();

  const keyFactsParams = new URLSearchParams(location.search);
  keyFactsParams.set('viewKeyFacts', 'true');
  const keyFactsUrl = `/matters/${matterId}/subsets/${subsetId}/facts?${keyFactsParams.toString()}`;

  const relevantFactsParams = new URLSearchParams(location.search);
  relevantFactsParams.set('viewRelevantFacts', 'true');
  const relevantFactsUrl = `/matters/${matterId}/subsets/${subsetId}/facts?${relevantFactsParams.toString()}`;

  if (!isLoading && !overview) {
    return null;
  }

  return (
    <div className="h-full lg:col-span-3">
      <div className="flex h-full flex-col rounded-md border border-gray-200 bg-white">
        <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 p-3 px-4">
          <div className="flex items-center gap-3">
            <Icon className="lucide--list h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-medium text-gray-700">Facts & Inconsistencies</h3>
          </div>
        </div>

        <div className="flex-1 p-4">
          {isLoading ? (
            <FactsOverViewLoader />
          ) : (
            <div className="flex h-full flex-col justify-between gap-4">
              <div className="space-y-3">
                {overview?.documents !== undefined && (
                  <p className="text-xs text-gray-500">
                    Data extracted from the <b>{overview.documents}</b>{' '}
                    {overview.documents === 1 ? 'document' : 'documents'} in this subset:
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
                  {overview?.totalFacts !== undefined && (
                    <Button
                      asChild
                      className="border border-gray-200 bg-gray-50 text-gray-500 duration-150 hover:bg-gray-100 hover:text-gray-600 [&_.iconify]:text-gray-500 [&_.iconify:hover]:text-gray-600"
                      size="sm"
                    >
                      <Link to={`/matters/${matterId}/subsets/${subsetId}/facts`}>
                        <span className="text-gray-500">Total Facts:</span>
                        <span className="font-medium text-gray-700">{overview.totalFacts}</span>
                        <Icon className="lucide--arrow-right h-3 w-3" />
                      </Link>
                    </Button>
                  )}
                  {overview?.keyFacts !== undefined && (
                    <Button
                      asChild
                      className="border border-amber-200 bg-amber-50/50 text-amber-700/70 duration-150 hover:bg-amber-50 hover:text-amber-800 [&_.iconify]:text-amber-700/70 [&_.iconify:hover]:text-amber-800"
                      size="sm"
                    >
                      <Link to={keyFactsUrl} onClick={() => trackCustomEvent(TrackingEvents.ViewKeyFactsFromSummary)}>
                        <Icon className="lucide--key h-3 w-3 text-amber-700/70" />
                        <span>Key Facts:</span>
                        <span className="font-medium">{overview.keyFacts}</span>
                        <Icon className="lucide--arrow-right h-3 w-3" />
                      </Link>
                    </Button>
                  )}
                  {overview?.relevantFacts !== undefined && (
                    <Button
                      asChild
                      className="border border-emerald-200 bg-emerald-50/50 text-emerald-700/70 duration-150 hover:bg-emerald-50 hover:text-emerald-800 [&_.iconify]:text-emerald-700/70 [&_.iconify:hover]:text-emerald-800"
                      size="sm"
                    >
                      <Link to={relevantFactsUrl}>
                        <Icon className="lucide--check h-3 w-3" />
                        <span>Relevant Facts:</span>
                        <span className="font-medium">{overview.relevantFacts}</span>
                        <Icon className="lucide--arrow-right h-3 w-3" />
                      </Link>
                    </Button>
                  )}
                  {overview?.relevantFacts !== undefined && (
                    <Button
                      asChild
                      className="border border-rose-200 bg-rose-50/50 text-rose-700/70 duration-150 hover:bg-rose-50 hover:text-rose-800 [&_.iconify]:text-rose-700/70 [&_.iconify:hover]:text-rose-800"
                      size="sm"
                    >
                      <Link to={`/matters/${matterId}/subsets/${subsetId}/inconsistencies`}>
                        <Icon className="lucide--triangle-alert h-3 w-3" />
                        <span>Inconsistencies:</span>
                        <span className="font-medium">{totalInconsistencies}</span>
                        <Icon className="lucide--arrow-right h-3 w-3" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>

              <Button className="w-full" variant="primary" onClick={onCreateChronology}>
                <Icon className="lucide--git-fork h-4 w-4" />
                Create Chronology
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
